import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {StateStorageService} from './state-storage.service';
import {marker as _} from '@biesbjerg/ngx-translate-extract-marker';
import {AlertController, NavController, ToastButton, ToastController} from '@ionic/angular';
import {HttpErrorResponse} from "@angular/common/http";
import {User} from "./common-datatypes";
import {isNumeric} from "rxjs/internal-compatibility";

const MESSAGE_TIMEOUT = 3000;  // ms

@Injectable({
    providedIn: 'root'
})
export class CommonFunctionsService {

    private errorCounter: number = 0;
    private messages: { [index: string]: string; } | undefined;

    constructor(private navController: NavController,
                private appState: StateStorageService,
                private translate: TranslateService,
                private alertController: AlertController,
                private toastController: ToastController) {

    }

    asCustomEvent(val: Event): CustomEvent {
        return <CustomEvent>val;
    }

    showHttpError(error: HttpErrorResponse|undefined, otherMessage: string = '', timeout: number = 2 * MESSAGE_TIMEOUT): boolean {
        if (!this.messages) {  // message translations
            this.messages = {};
            this.getTranslations(this.messages, [
                'NONCE_HIJACK', 'SESSION_TIMED_OUT', 'INVALID_CREDENTIALS', 'AUTH_MISSING',
                'ERROR_IN_PERSISTENCE_LAYER', 'ERROR_IN_PROVIDED_DATA'], 'error.');
        }
        let errorError = '';
        let errorMessage = '';
        let humanMessage = '';
        if (error) { // get most relevant error message
            if (error.message) {
                errorMessage = error.message;
            }
            if (error.statusText) {
                errorError = error.statusText;
            }
            if (error.error) {
                if (typeof error.error === 'string' ) errorError = error.error;
                if (error.error.error && (typeof error.error.error === 'string' )) errorError = error.error.error;
                if (error.error.message && (typeof error.error.message === 'string' )) errorMessage = error.error.message;
            }
            const fullError = errorError + ' ' + errorMessage;

            if (errorError || errorMessage) this.appState.lastError = fullError;
            if (error.status) {
                this.appState.lastHttpStatusCode = error.status;

                if (0 === error.status) { // 0 unknown HTTP Error either cors (should not happen) or ssl
                    humanMessage = 'CORS or SSL Error';
                } else if (401 === error.status) { // authentication
                    if (-1 !== fullError.search(/AUTH_MISSING/g)) {
                        this.appState.nonceMissing = true;
                        // hide if not logged in
                        if (this.appState.loggedIn) humanMessage = this.messages['AUTH_MISSING'];
                        else this.appState.lastError = '';
                    } else if (-1 !== fullError.search(/NONCE_HIJACK/g)) {
                        if (this.appState.loggedIn) humanMessage = this.messages['NONCE_HIJACK'];
                    } else if (-1 !== fullError.search(/INVALID_CREDENTIALS/g)) {
                        humanMessage = this.messages['INVALID_CREDENTIALS'];
                    } else if (-1 !== fullError.search(/SESSION_TIMED_OUT/g)) {
                        humanMessage = this.messages['SESSION_TIMED_OUT'];
                    } else {
                        if (this.appState.loggedIn) humanMessage = 'Authentication Error';
                    }
                    // send user to login if unauthenticated
                    setTimeout(() => this.navController.navigateRoot('/login'), 100);
                } else if (403 === error.status) { // authorization
                    humanMessage = 'Authorization Error';
                } else if (406 === error.status) { // Api / DB Error
                    if (-1 !== fullError.search(/ERROR_IN_PERSISTENCE_LAYER/g)) {
                        humanMessage = this.messages['ERROR_IN_PERSISTENCE_LAYER'];
                    } else if (-1 !== fullError.search(/ERROR_IN_PROVIDED_DATA/g)) {
                        humanMessage = this.messages['ERROR_IN_PROVIDED_DATA'];
                    } else {
                        humanMessage = 'Api / DB Error';
                    }
                } else if (5 === Math.floor(error.status / 100)) { // server error
                    humanMessage = 'Server Error';
                } else {
                    humanMessage = 'unknown Error';
                }
            } else humanMessage = otherMessage;
        } else humanMessage = otherMessage;

        if (humanMessage) {
            humanMessage += ' \n' + errorError + ' \n' + errorMessage;
            this.appState.lastError = humanMessage;
            console.log(error);
            this.showError(humanMessage, '', timeout);
            return true;
        }
        return false;
    }

    showError(header: string, message: string = '', timeoutOk: number | string = MESSAGE_TIMEOUT): Promise<void> {
        this.errorCounter += 1;
        header += ' (' + this.errorCounter + ')';
        console.log('Toast Error: ' + header + ' : ' + message);
        let timeout: number;
        let buttons: ToastButton[] = [];
        if (isNumeric(timeoutOk)) timeout = <number>timeoutOk;
        else {
            timeout = 0;
            buttons = [{}, {text: <string>timeoutOk}]
        }
        return this.toastController.create({
            header: header,
            message: message,
            cssClass: 'preLineWhitespace',
            buttons: buttons,
            duration: timeout
        }).then((toast) => {
            toast.onDidDismiss().then(() => {
                this.errorCounter -= 1;
            })
            return toast.present();
        });
    }

    showMessage(header: string, message: string = '', timeoutOk: number | string = MESSAGE_TIMEOUT) {
        this.errorCounter += 1;
        header += ' (' + this.errorCounter + ')';
        console.log('Toast Message: ' + header + ' : ' + message);
        let timeout: number;
        let buttons: ToastButton[] = [];
        if (isNumeric(timeoutOk)) timeout = <number>timeoutOk;
        else {
            timeout = 0;
            buttons = [{}, {text: <string>timeoutOk}]
        }
        this.toastController.create({
            header: header,
            message: message,
            cssClass: 'preLineWhitespace',
            buttons: buttons,
            duration: timeout
        }).then((toast) => {
            toast.onDidDismiss().then(() => {
                this.errorCounter -= 1;
            })
            toast.present();
        });
    }

    getTranslations(dest: { [index: string]: string; }, keys: string[], prefix: string = ''): void {
        const prefixLen = prefix.length;
        if (0 !== prefixLen) {
            keys.forEach((value: string, index: number) => {
                keys[index] = prefix + value;
            });
        }
        this.translate.get(keys).subscribe((translation: { [index: string]: string; }) => {
            for (const field of Object.keys(translation)) {
                let localField = field;
                if (0 !== prefixLen) localField = field.substring(prefixLen);
                dest[localField] = translation[field];

            }
        });
    }

    getSingleTranslation(key: string): Promise<string> {
        return this.translate.get(key).toPromise();
    }

    getDeviceDetailTranslations(messagesDate: { [index: string]: string; }, messagesDeviceStatus: { [index: string]: string; },
                                messagesOwnership: { [index: string]: string; },
                                messagesLocationeStatus: { [index: string]: string; }, messagesSap: { [index: string]: string; }) {

        /* extracted from template */
        this.getTranslations(messagesDate, ['from', 'since', 'to'], 'date.');

        _('device_status.USABLE');
        _('device_status.FAULTY');
        _('device_status.FAULTY_EXCHANGED');
        _('device_status.FAULTY_DISPOSED');
        _('device_status.DISPOSED_OLD_AGE');
        _('device_status.DISPOSED');
        _('device_status.LEASING_ENDED');
        _('device_status.LOST');
        _('device_status.SOLD');
        _('device_status.GONE');
        this.getTranslations(messagesDeviceStatus,
            ['DISPOSED', 'DISPOSED_OLD_AGE', 'FAULTY', 'FAULTY_DISPOSED',
                'FAULTY_EXCHANGED', 'LEASING_ENDED', 'LOST', 'SOLD', 'USABLE', 'GONE'], 'device_status.');

        _('ownership.BOUGHT');
        _('ownership.LEASED');
        _('ownership.LENT');
        _('ownership.DONATION');
        this.getTranslations(messagesOwnership, ['BOUGHT', 'LEASED', 'LENT', 'DONATION'], 'ownership.');

        _('location_status.ZKM_IN_USE');
        _('location_status.ZKM_STORED');
        _('location_status.STOCK_IN_USE');
        _('location_status.STOCK_STORED');
        _('location_status.EXT_IN_USE');
        _('location_status.EXT_STORED');
        _('location_status.LENT');
        _('location_status.RESERVED');
        _('location_status.GONE');
        this.getTranslations(messagesLocationeStatus,
            ['ZKM_IN_USE', 'ZKM_STORED', 'STOCK_IN_USE', 'STOCK_STORED', 'EXT_IN_USE', 'EXT_STORED',
                'LENT', 'RESERVED', 'GONE'],
            'location_status.');

        this.getTranslations(messagesSap, ['none'], 'sap_number.none');
    }

    getSortTranslations(messages: { [index: string]: string; }) {
        _('select.sort.column.name'); // ngx-translate-extract
        _('select.sort.column.all');
        _('select.sort.direction.name');
        _('select.sort.direction.all');
        _('select.sort.invnr');
        _('select.sort.last_edit');
        _('select.sort.id');
        _('select.sort.category');
        _('select.sort.manufacturer');
        _('select.sort.model');
        _('select.sort.device_status');
        _('select.sort.ownership');
        _('select.sort.owner');
        _('select.sort.department');
        _('select.sort.buy_date');
        _('select.sort.tuev_date');
        _('select.sort.sap_nr');
        _('select.sort.location_status');
        _('select.sort.start_date');
        _('select.sort.end_date');
        _('select.sort.taker');
        this.getTranslations(messages,
            ['invnr', 'last_edit', 'id', 'category', 'manufacturer', 'model', 'device_status', 'ownership', 'owner',
                'department', 'buy_date', 'tuev_date', 'sap_nr', 'location_status', 'start_date', 'end_date', 'taker'],
            'select.sort.');
    }

    private testNotSonstigeOrEmpty(cat_one: string, cat_two: string, full: boolean): boolean {
        return !((!cat_one) || (0 >= cat_one.length) ||
            (!full && (('sonstige' === cat_one) || ('Sonstiges' === cat_one)) && (0 >= cat_two.length)));
    }

    public combinedCategoryName(device: { cat_0: string, cat_1: string, cat_2: string, cat_3: string }, full: boolean = false): string {
        if (!device) return '';
        let categoryName = device.cat_0;
        if (this.testNotSonstigeOrEmpty(device.cat_1, device.cat_2, full)) {
            categoryName += ', ' + device.cat_1;
            if (this.testNotSonstigeOrEmpty(device.cat_2, device.cat_3, full)) {
                categoryName += ', ' + device.cat_2;
                if (this.testNotSonstigeOrEmpty(device.cat_3, '', full)) {
                    categoryName += ', ' + device.cat_3;
                }
            }
        }
        return categoryName;
    }

    public shortCombinedCategoryName(device: { cat_0: string, cat_1: string, cat_2: string, cat_3: string }): string {
        if (!device) return '';
        let categoryName = '';
        if (!this.testNotSonstigeOrEmpty(device.cat_2, device.cat_3, false)) categoryName += device.cat_0;
        if (this.testNotSonstigeOrEmpty(device.cat_1, device.cat_2, false)) {
            if (!this.testNotSonstigeOrEmpty(device.cat_3, '', false)) {
                if ('' < categoryName) categoryName += ', ';
                categoryName += device.cat_1;
            }
            if (this.testNotSonstigeOrEmpty(device.cat_2, device.cat_3, false)) {
                if ('' < categoryName) categoryName += ', ';
                categoryName += device.cat_2;
                if (this.testNotSonstigeOrEmpty(device.cat_3, '', false)) {
                    categoryName += ', ' + device.cat_3;
                }
            }
        }
        return categoryName;
    }

    public repairUrl(oldUrl: string): string {
        if (0 === oldUrl.indexOf('http')) {
            return oldUrl;
        }
        return 'https://' + oldUrl;
    }

    stitchUserRole(user: User): number {
        let role_id_bits = 0;
        for (let i = 0; i < this.appState.userRoles.length; i++) {
            switch (this.appState.userRoles[i].name) {
                case 'SEARCH':
                    if (user.search) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'EXPORT':
                    if (user.export) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'LOCATION_CHANGE':
                    if (user.relocate) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'LOCATION_CHANGE_FOREIGN':
                    if (user.relocateForeign) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'LEND':
                    if (user.lend) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'LEND_FOREIGN':
                    if (user.lendForeign) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'EDIT':
                    if (user.edit) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'EDIT_FOREIGN':
                    if (user.editForeign) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'LABEL':
                    if (user.label) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'HISTORY':
                    if (user.history) role_id_bits += +this.appState.userRoles[i].id;
                    break;
                case 'ADMIN':
                    if (user.admin) role_id_bits += +this.appState.userRoles[i].id;
                    break;
            }
        }
        return role_id_bits;
    }

    /* tslint:disable:no-bitwise*/
    private testAuth(role: string, roleId: number): boolean {
        for (let i = 0; i < this.appState.userRoles.length; i++) {
            if (role === this.appState.userRoles[i].name) {
                return (+this.appState.userRoles[i].id === (roleId & +this.appState.userRoles[i].id));
            }
        }
        return false;
    }

    /* tslint:enable:no-bitwise*/
    public hasAuthRole(roles: string | string[], roleId: number): boolean {
        if (!this.appState.userRoles) return false;

        if (typeof roles === 'string') return this.testAuth(roles, roleId);

        for (let i = 0; i < roles.length; i++) {
            if (!this.testAuth(roles[i], roleId)) return false;
        }
        return true;
    }

    public authOnlySearch(): boolean {
        if (!this.appState.userRoles) return false;

        for (let i = 0; i < this.appState.userRoles.length; i++) {
            if ('SEARCH' === this.appState.userRoles[i].name) {
                return this.appState.authData.role === this.appState.userRoles[i].id;

            }
        }
        return false;
    }

    public authRelocate(department: string): boolean {
        let neededRole = 'LOCATION_CHANGE';
        if (department !== this.appState.authData.department) neededRole = 'LOCATION_CHANGE_FOREIGN';
        return this.hasAuthRole(neededRole, this.appState.authData.role);
    }

    public authLend(department: string): boolean {
        let neededRole = 'LEND';
        if (department !== this.appState.authData.department) neededRole = 'LEND_FOREIGN';
        return this.hasAuthRole(neededRole, this.appState.authData.role);
    }

    public authEdit(department: string): boolean {
        let neededRole = 'EDIT';
        if (department !== this.appState.authData.department) neededRole = 'EDIT_FOREIGN';
        return this.hasAuthRole(neededRole, this.appState.authData.role);
    }

    public authDuplicate(): boolean {
        return this.hasAuthRole('EDIT', this.appState.authData.role);
    }

    public fieldValueToName(item: { [index: string]: string; }, value: string): string {
        return (<string[]>Object.keys(item)).find(key => item[key] === value) ?? '';
    }

    private confirmationAlert(title: string, message: string, yes: string, no: string, yeshandler: any): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.alertController.create({
                header: title,
                message: message,
                buttons: [{
                    text: no,
                    handler: () => {
                        resolve(false);
                    }
                }, {
                    text: yes,
                    handler: () => {
                        resolve(true);
                        yeshandler();
                    }
                }]
            }).then((alert) => {
                alert.present();
            });
        });
    }

    public askCancelEdit(): Promise<boolean> {
        _('edit.cancel-titel');
        _('edit.cancel-message');
        _('edit.cancel-yes');
        _('edit.cancel-no');
        const messages = {titel: '', message: '', yes: '', no: ''};
        this.getTranslations(messages, ['titel', 'message', 'yes', 'no'], 'edit.cancel-');
        const yeshandler = () => {
            this.appState.editAction = false;
        };
        return this.confirmationAlert(messages['titel'], messages['message'], messages['yes'], messages['no'], yeshandler);
    }

}
